<template>
  <main class="auth-page register-page">
    <section class="auth-showcase">
      <router-link to="/" class="brand" aria-label="123Mail"><img src="@/assets/img/icon.png" alt="" /><span>123MAIL</span></router-link>
      <div class="showcase-copy">
        <span class="eyebrow">Comece agora</span>
        <h1>Mais proximidade. Mais resultados.</h1>
        <p>Crie sua conta e tenha as ferramentas certas para acompanhar cada contato da sua base.</p>
        <div class="metric-card">
          <div><strong>1</strong><span>painel completo</span></div>
          <div><strong>3x</strong><span>mais produtividade</span></div>
          <div><strong>24h</strong><span>por dia online</span></div>
        </div>
      </div>
      <p class="showcase-footer">Configuração simples e rápida.</p>
    </section>

    <section class="auth-panel register-panel">
      <div class="auth-card register-card">
        <div class="mobile-brand"><img src="@/assets/img/icon.png" alt="" /><strong>123MAIL</strong></div>
        <header class="auth-header">
          <span class="eyebrow">Crie sua conta</span>
          <h2>Vamos começar?</h2>
          <p>Preencha seus dados para criar o acesso à plataforma.</p>
        </header>

        <validation-observer ref="form" v-slot="{ handleSubmit }">
          <form @submit.prevent="handleSubmit(register)" novalidate>
            <div class="form-grid">
              <validation-provider name="nome" rules="required" v-slot="{ errors }">
                <div class="field" :class="{ 'has-error': errors[0] }">
                  <label for="register-name">Nome completo</label>
                  <div class="input-shell"><i class="far fa-user"></i><input id="register-name" v-model="name" type="text" autocomplete="name" placeholder="Seu nome" /></div>
                  <span class="field-error">{{ errors[0] }}</span>
                </div>
              </validation-provider>

              <validation-provider name="email" rules="required|email" v-slot="{ errors }">
                <div class="field" :class="{ 'has-error': errors[0] }">
                  <label for="register-email">E-mail profissional</label>
                  <div class="input-shell"><i class="far fa-envelope"></i><input id="register-email" v-model="email" type="email" autocomplete="email" placeholder="voce@empresa.com.br" /></div>
                  <span class="field-error">{{ errors[0] }}</span>
                </div>
              </validation-provider>

              <validation-provider name="telefone" rules="required" v-slot="{ errors }">
                <div class="field" :class="{ 'has-error': errors[0] }">
                  <label for="register-phone">Celular (WhatsApp)</label>
                  <div class="input-shell"><i class="fab fa-whatsapp"></i><the-mask id="register-phone" v-model="phone" :mask="['(##) ####-####', '(##) #####-####']" autocomplete="tel" placeholder="(00) 00000-0000" /></div>
                  <span class="field-error">{{ errors[0] }}</span>
                </div>
              </validation-provider>

              <validation-provider name="senha" vid="password" rules="required|min:8" v-slot="{ errors }">
                <div class="field" :class="{ 'has-error': errors[0] || passwordValidator }">
                  <label for="register-password">Senha</label>
                  <div class="input-shell">
                    <i class="fas fa-lock"></i><input id="register-password" v-model="password" :type="showPassword ? 'text' : 'password'" autocomplete="new-password" placeholder="Mínimo de 8 caracteres" />
                    <button type="button" class="password-toggle" :aria-label="showPassword ? 'Ocultar senhas' : 'Mostrar senhas'" @click="showPassword = !showPassword"><i :class="showPassword ? 'far fa-eye-slash' : 'far fa-eye'"></i></button>
                  </div>
                  <span class="field-error">{{ errors[0] || passwordValidator }}</span>
                </div>
              </validation-provider>

              <validation-provider name="repita a senha" rules="required|confirmed:password" v-slot="{ errors }">
                <div class="field" :class="{ 'has-error': errors[0] }">
                  <label for="register-password-confirmation">Confirme sua senha</label>
                  <div class="input-shell"><i class="fas fa-lock"></i><input id="register-password-confirmation" v-model="password2" :type="showPassword ? 'text' : 'password'" autocomplete="new-password" placeholder="Digite novamente" /></div>
                  <span class="field-error">{{ errors[0] }}</span>
                </div>
              </validation-provider>
            </div>

            <p class="terms-copy">Ao continuar, você concorda com os termos de uso e a política de privacidade da 123Mail.</p>
            <button type="submit" class="submit-button" :disabled="loading">
              <i v-if="loading" class="fas fa-circle-notch fa-spin"></i><span>{{ loading ? 'Criando sua conta...' : 'Criar minha conta' }}</span><i v-if="!loading" class="fas fa-arrow-right"></i>
            </button>
          </form>
        </validation-observer>
        <p class="auth-switch">Já possui uma conta? <router-link to="/">Fazer login</router-link></p>
      </div>
    </section>
  </main>
</template>

<script>
import { createAccount } from "@/services/auth";

export default {
  name: "Register",
  data() {
    return { name: "", email: "", phone: "", password: "", password2: "", loading: false, showPassword: false };
  },
  computed: {
    passwordValidator() {
      return this.password && this.password.length < 8 ? "Mínimo de 8 caracteres" : null;
    },
  },
  methods: {
    register() {
      if (this.loading) return;
      this.loading = true;
      createAccount({ name: this.name.trim(), email: this.email.trim(), phone: this.phone, password: this.password })
        .then((response) => {
          this.trackingEvents();
          this.$store.dispatch("auth", { token: response.auth_token, account: response.account_id });
          this.$toast.success({ title: "Sucesso", message: "Conta criada com sucesso" });
          this.$router.push({ name: "Account" });
        })
        .catch((error) => {
          if (error && error.message === "request-canceled") return;
          const description = error && error.response && error.response.data && error.response.data.description;
          if (["exist-account", "exist-user"].includes(description)) {
            this.$toast.error({ title: "Conta já cadastrada", message: "Já existe um acesso com este e-mail. Recupere a senha ou utilize outro e-mail." });
            return;
          }
          this.$toast.error({ title: "Erro", message: "Não foi possível criar sua conta. Tente novamente." });
        })
        .finally(() => { this.loading = false; });
    },
    trackingEvents() {
      const fbq = window.fbq;
      if (fbq) fbq("track", "Lead");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./auth-shared";
</style>
